"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { toggleWishlist, isInWishlist } from "@/lib/api/wishlist";
import { useToast } from "@/hooks/use-toast";
import { createClient } from "@/lib/supabase/client";

interface UseWishlistParams {
  productId: string;
  isAuthenticated: boolean;
  onAuthRequired?: () => void;
  /**
   * 같은 화면의 다른 찜 버튼(상세 이미지 위 하트 등)과 상태를 맞출 때 쓴다.
   * value 가 바뀌면 따라가고, 조회·토글 결과는 onChange 로 올린다.
   */
  value?: boolean;
  onChange?: (isWishlisted: boolean) => void;
}

export function useWishlist({
  productId,
  isAuthenticated,
  onAuthRequired,
  value,
  onChange,
}: UseWishlistParams) {
  const t = useTranslations("product.booking");
  const { toast } = useToast();

  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isWishlistLoading, setIsWishlistLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && productId) {
      isInWishlist(productId).then((inWishlist) => {
        setIsWishlisted(inWishlist);
        onChange?.(inWishlist);
      });
    }
    // onChange 는 부모 setState(안정 참조) — 조회는 인증/상품이 바뀔 때만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, productId]);

  useEffect(() => {
    if (value !== undefined) setIsWishlisted(value);
  }, [value]);

  const handleWishlistClick = async () => {
    // 재진입 가드는 첫 await(getClaims) 이전에 걸어야 한다 — 그 뒤에 걸면
    // 인증 확인 왕복 동안 버튼이 살아 있어 연타가 toggle을 두 번 태운다.
    if (isWishlistLoading) return;
    setIsWishlistLoading(true);

    try {
      // 실시간 인증 확인: isAuthenticated prop은 부모의 user 상태가 비동기로
      // 로드되기 전 클릭하면 stale(false)일 수 있다. 캐시된 세션 기반 getClaims()로
      // 클릭 시점에 다시 확인한다 (WishlistButton.handleClick과 동일 패턴).
      const supabase = createClient();
      const { data } = await supabase.auth.getClaims();
      if (!data?.claims?.sub) {
        if (onAuthRequired) {
          onAuthRequired();
        } else {
          toast({
            title: t("loginRequired") || "로그인이 필요합니다",
            description:
              t("loginToAddWishlist") ||
              "위시리스트에 추가하려면 먼저 로그인해주세요.",
            variant: "destructive",
          });
        }
        return;
      }

      const result = await toggleWishlist(productId);

      if (result.error) {
        toast({
          title: t("error") || "오류",
          description: result.error,
          variant: "destructive",
        });
        return;
      }

      setIsWishlisted(result.isInWishlist);
      onChange?.(result.isInWishlist);

      toast({
        title: result.isInWishlist
          ? t("addedToWishlist") || "위시리스트에 추가됨"
          : t("removedFromWishlist") || "위시리스트에서 삭제됨",
        description: result.isInWishlist
          ? t("addedToWishlistDesc") || "상품이 위시리스트에 추가되었습니다."
          : t("removedFromWishlistDesc") ||
            "상품이 위시리스트에서 삭제되었습니다.",
      });
    } catch (error) {
      console.error("Failed to toggle wishlist:", error);
      toast({
        title: t("error") || "오류",
        description:
          t("wishlistError") || "위시리스트 처리 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsWishlistLoading(false);
    }
  };

  return { isWishlisted, isWishlistLoading, handleWishlistClick };
}
